<script setup lang="ts">
import HistoryCard from '@/components/Profile/HistoryCard.vue';
import LoadingBar from '@/components/Skeletons/LoadingBar.vue';
import { useFetch } from '@/composables/useFetch.js';
import { useProfileDishLists } from '@/composables/useProfileDishLists.js';
import type { MessageSchema } from '@/i18n';
import { useEnvStore } from '@/stores/envStore.js';
import type { DishHistoryResponse } from '@/types/api.js';
import type { Dish } from '@/types/dish.js';
import type { DishHistoryState } from '@/types/history.js';
import AutoComplete from 'primevue/autocomplete';
import Button from 'primevue/button';
import { useToast } from 'primevue/usetoast';
import { nextTick, onMounted, ref, watch, watchEffect, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import ImportDish from '../../components/Profile/ImportDish.vue';
import LoadingView from '../LoadingView.vue';

const { t } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });

const router = useRouter();
const toast = useToast();

const { fetchData } = useFetch();

const apiUrl = useEnvStore().apiUrl;

const searchQuery = ref('');
const isSearching = ref(false);
const createdUsing: Ref<string[]> = ref(['ocr']);

const isFetchingList = ref(false);
const isAnalyzing = ref(false);

const nextPageUrl = ref(`${apiUrl}/dishes`);
const loadedDishes = ref(0);
const totalDishes = ref(0);

const props = defineProps<{
  selectMode?: boolean;
}>();

// When the users wants to add a dish from the planning.
const emit = defineEmits<{
  // (e: 'addDishToPlan', dish: Dish): void
  (e: 'dish-clicked', dish: Dish): void;
}>();

const { dishes, handleDuplication, handleDeletion, handleFavoriteChange } = useProfileDishLists(toast);

function handleCardClick(id: number) {
  const dish = dishes.value.find((dish) => dish.id === id) as Dish;
  if (props.selectMode) {
    emit('dish-clicked', dish);
    return;
  } else {
    saveState();
    router.push(`/menu/dishes/${id}`);
  }
}

watchEffect(() => console.log(dishes.value.length));
async function getDishes() {
  // This var is needed because the isFetching var from composable
  // Makes a conflict between the getDishes and the searchDishes methods
  isFetchingList.value = true;

  const { data, error } = await fetchData(nextPageUrl.value, 'GET');
  if (error) {
    console.log('error', error);
  } else {
    const response = data as DishHistoryResponse;
    console.log('response', response);
    dishes.value = [...dishes.value, ...response.data];
    loadedDishes.value = response.to;
    totalDishes.value = response.total;
    nextPageUrl.value = response.next_page_url ?? '';
  }

  isFetchingList.value = false;
}

// This method is only triggered when we type in the serach bar or when the checkboxes are clicked.
// The pagination is the handled by getDishes
async function filterDishes() {
  // isSearching is needed to display search icon when the method is triggered with the watcher
  if (loadedDishes.value === 0) {
    isFetchingList.value = true;
  } else {
    isSearching.value = true;
  }

  // const searchUrl = `${apiUrl}/dishes/search?name=${encodeURIComponent(searchQuery.value)}`
  const searchUrl = `${apiUrl}/dishes/search?${new URLSearchParams({
    name: searchQuery.value,
    created_using: createdUsing.value.join(','),
    is_planned: 'false',
  })}`;

  const { data, error } = await fetchData(searchUrl, 'GET');

  if (error) {
    console.log('error', error);
  } else {
    const response = data as DishHistoryResponse;
    dishes.value = response.data;
    loadedDishes.value = response.to;
    totalDishes.value = response.total;
    nextPageUrl.value = response.next_page_url ?? '';
  }
  isSearching.value = false;
  isFetchingList.value = false;
}

const scrollPosition = ref(0);

function saveState() {
  const stateToSave: DishHistoryState = {
    dishes: dishes.value,
    loadedDishes: loadedDishes.value,
    totalDishes: totalDishes.value,
    nextPageUrl: nextPageUrl.value,
    scrollPosition: window.scrollY,
    searchQuery: searchQuery.value,
    createdUsing: createdUsing.value,
  };
  sessionStorage.setItem('myScansState', JSON.stringify(stateToSave));
}

function restoreState() {
  dishes.value = [];
  const savedState = sessionStorage.getItem('myScansState');
  if (savedState) {
    const state: DishHistoryState = JSON.parse(savedState);
    dishes.value = state.dishes;
    loadedDishes.value = state.loadedDishes;
    totalDishes.value = state.totalDishes;
    nextPageUrl.value = state.nextPageUrl;
    scrollPosition.value = state.scrollPosition;
    searchQuery.value = state.searchQuery;
    createdUsing.value = state.createdUsing || []; // Provide default empty array if not found
    console.log(scrollPosition.value);
    nextTick(() => {
      window.scrollTo(0, scrollPosition.value);
    });
    sessionStorage.removeItem('myScansState');
  } else {
    filterDishes();
  }
}

watch(searchQuery, () => {
  if (searchQuery.value === '') {
    filterDishes();
  }
});

watch(isAnalyzing, () => {
  console.log('IS ANALYSEING ', isAnalyzing.value);
});

onMounted(() => {
  restoreState();
});
</script>

<template>
  <main>
    <div v-if="!isAnalyzing">
      <AutoComplete
        v-model="searchQuery"
        class="mb-6"
        panel-class="hidden"
        :suggestions="dishes"
        :loading="isSearching"
        :placeholder="t('common.searchPlaceHolder')"
        @complete="filterDishes"
      />
      <div v-if="dishes.length === 0 && !isFetchingList && !isSearching" class="my-8 text-center text-gray-500">
        <p class="text-sm italic">{{ t('profile.dishLists.noScannedDishes') }}</p>
      </div>
      <HistoryCard
        v-for="dish in dishes"
        :key="dish.id"
        :dish="dish"
        :select-mode="props.selectMode"
        @card-clicked="handleCardClick(dish.id)"
        @handle-deletion="(wasDeleted: boolean) => handleDeletion(wasDeleted, dish.id)"
        @handle-duplication="(duplicationResponse: null | Dish) => handleDuplication(duplicationResponse, dish.id)"
        @handle-favorite-change="(favoriteResponse: boolean | null) => handleFavoriteChange(favoriteResponse, dish.id)"
      />
      <Button v-if="loadedDishes < totalDishes" :loading="isFetchingList && dishes.length !== 0" :label="t('common.seeMore')" @click="getDishes()" />
    </div>
    <LoadingView v-if="isFetchingList && dishes.length === 0" />
    <LoadingView v-else-if="isAnalyzing" :generation="true" />
    <LoadingBar v-if="isAnalyzing" class="mb-3 mt-24" :duration-in-seconds="40" />
    <ImportDish v-show="!isAnalyzing" @update-analyze-status="isAnalyzing = $event" />
  </main>
</template>
